import { useState, type FC } from 'react';
import { type DefaultOptionType } from 'antd/es/select';
import { Select, Space, Button } from '@g2u/ui-kit';
import { isEmpty } from 'lodash';
import { Form } from 'antd';

import { JsonDataItem, type WithOptionalDataTest } from 'types';
import styles from './notifications-fields.module.scss';

type Props = {
  emailOptions?: DefaultOptionType[];
  smsOptions?: DefaultOptionType[];
  initialValues?: NotificationsFields.InitialValues;
  onSubmit?: (data: JsonDataItem | null) => void;
  actionTitle?: string;
  isLoading?: boolean;
  isNotified?: boolean;
  hasSkipButton?: boolean;
  isStandalone?: boolean;
  actionButtonTheme?: Button.Type;
} & WithOptionalDataTest;

export const NotificationsFields: FC<Props> = (props) => {
  const {
    initialValues,
    emailOptions,
    smsOptions,
    onSubmit,
    actionTitle,
    isLoading,
    isNotified,
    isStandalone,
    actionButtonTheme,
    hasSkipButton,
  } = props;
  const [isFormValid, setIsFormValid] = useState<boolean>(true);
  const [form] = Form.useForm();

  const handleValuesChange = (_: unknown, allValues: JsonDataItem) => {
    setIsFormValid(!(isEmpty(allValues.emailNotification) && isEmpty(allValues.smsNotification)));
  };

  const fields = (
    <>
      <Select.$
        fullWidth
        showSearch={false}
        mode="multiple"
        label="Send Email to: "
        name="emailNotification"
        disabled={isEmpty(emailOptions)}
        options={emailOptions}
        initialValue={initialValues?.emailNotification}
        dataTestId="to-email-notification"
        data-dd-privacy="mask"
      />
      <Select.$
        fullWidth
        showSearch={false}
        mode="multiple"
        label="Send SMS to: "
        name="smsNotification"
        disabled={isEmpty(smsOptions)}
        options={smsOptions}
        initialValue={initialValues?.smsNotification}
        dataTestId="to-number-notification"
        data-dd-privacy="mask"
      />
    </>
  );

  if (!isStandalone) {
    return fields;
  }

  return (
    <Form form={form} colon={false} layout="horizontal" onValuesChange={handleValuesChange} onFinish={onSubmit}>
      <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle} className={styles.notificationFields}>
        {fields}
      </Space.$>
      <Space.$ size={Space.Size.Middle}>
        {isNotified ? (
          <Button.$
            loading={isLoading}
            disabled={!isFormValid}
            onClick={() => form.submit()}
            className={styles.notificationFields__action}
            dataTestId="resend-notification-btn"
          >
            Resend notifications
          </Button.$>
        ) : (
          <>
            <Button.$
              type={actionButtonTheme}
              loading={isLoading}
              disabled={!isFormValid}
              onClick={() => form.submit()}
              className={styles.notificationFields__action}
              dataTestId="send-notification-btn"
            >
              {actionTitle}
            </Button.$>
            {hasSkipButton && (
              <Button.$
                loading={isLoading}
                onClick={() => onSubmit?.(null)}
                className={styles.notificationFields__action}
                dataTestId="skip-notification-btn"
              >
                Skip sending notifications
              </Button.$>
            )}
          </>
        )}
      </Space.$>
    </Form>
  );
};

export namespace NotificationsFields {
  export type InitialValues = {
    emailNotification: string[];
    smsNotification: string[];
  };
}
